import { ReactNode } from 'react';
import {
	Accordion,
	Anchor,
	BackgroundImage,
	Badge,
	Burger,
	Button,
	Center,
	Checkbox,
	Collapse,
	Divider,
	Drawer,
	Group,
	LoadingOverlay,
	MantineProvider as Mantine,
	MantineTheme,
	MantineThemeOverride,
	Menu,
	Modal,
	NativeSelect,
	Overlay,
	Pagination,
	PasswordInput,
	Notification,
	Popover,
	Progress,
	Radio,
	RadioGroup,
	rem,
	ScrollArea,
	ScrollAreaAutosize,
	Select,
	Slider,
	Stack,
	Textarea,
	TextInput,
	Timeline,
	Title,
	Tooltip,
	Loader as MantineLoader,
	Image,
	Flex,
	Input,
	Card,
	Code,
	ThemeIcon,
	Table,
	FileInput,
	NumberInput,
	Avatar,
	Alert,
	List,
	Stepper,
} from '@mantine/core';
import { ModalsProvider } from '@mantine/modals';
import { Loader } from '@/components/partials/loading/partials/Loader';
import { Dropzone } from '@mantine/dropzone';
import { DateInput, DatePicker, DatePickerInput } from '@mantine/dates';
import {
	IconEye,
	IconInfoCircle,
	IconExclamationCircle,
	IconCheck,
} from '@tabler/icons-react';
import APP_CONFIG from '@/configs/appConfig';
import classNames from 'classnames';
import styles from '../sass/mantine.module.scss';
import '@mantine/core/styles.layer.css';
import '@mantine/dates/styles.layer.css';
import '@mantine/notifications/styles.css';
import '@mantine/carousel/styles.css';

const colors: MantineThemeOverride['colors'] = {
	red: [
		'#ffe9ed',
		'#ffd2d7',
		'#f9a2ac',
		'#f46f7f',
		'#f04559',
		'#ef2b40',
		'#ef1c34',
		'#dc0f28',
		'#be0521',
		'#a7001a',
	],
};

const components: MantineThemeOverride['components'] = {
	Flex: Flex.extend({
		defaultProps: {
			align: 'center',
		},
	}),
	Alert: Alert.extend({
		classNames: {
			title: styles.alertBody,
			body: styles.alertBody,
			message: styles.alertBody,
		},
	}),
	Pagination: Pagination.extend({
		defaultProps: {
			radius: 0,
			withControls: true,
			gap: 'sm',
		},
		classNames: {
			control: styles.paginationControl,
			dots: styles.paginationDots,
		},
	}),
	Burger: Burger.extend({
		defaultProps: {
			size: 30,
		},
	}),
	Code: Code.extend({
		defaultProps: {},
	}),
	ThemeIcon: ThemeIcon.extend({
		defaultProps: {
			radius: 0,
		},
	}),
	Badge: Badge.extend({
		defaultProps: {
			color: 'red',
			radius: 0,
		},
		classNames: {},
	}),
	Modal: Modal.extend({
		defaultProps: {
			centered: true,
			withCloseButton: true,
			radius: 0,
			zIndex: APP_CONFIG.Z_INDEX.MODAL,
			overlayProps: {
				opacity: 0.4,
				color: 'dark',
			},
			transitionProps: {
				transition: 'pop',
			},
			padding: 40,
			closeButtonProps: { size: 24 },
			size: `min(95dvw, ${rem(528)})`,
		},
		classNames: {
			header: styles.modalHeader,
			title: styles.modalTitle,
		},
	}),
	Button: Button.extend({
		defaultProps: {
			variant: 'outline',
			size: 'lg',
			radius: 0,
		},
	}),
	Menu: Menu.extend({
		defaultProps: {
			radius: 0,
			shadow: 'sm',
			transitionProps: {
				transition: 'fade',
			},
		},
		classNames: {
			item: styles.menuItem,
			itemLabel: styles.menuItemLabel,
			dropdown: styles.menuDropdown,
		},
	}),
	Collapse: Collapse.extend({
		defaultProps: {
			animateOpacity: true,
		},
	}),
	Table: Table.extend({
		defaultProps: {
			striped: true,
			withTableBorder: true,
			horizontalSpacing: 'sm',
		},
	}),
	Divider: Divider.extend({
		defaultProps: {
			size: 1,
			my: 'md',
		},
		classNames: {
			label: styles.dividerLabel,
		},
	}),
	Card: Card.extend({
		defaultProps: {
			radius: 0,
			p: 'xl',
			bg: 'white',
			bd: '1px solid var(--mantine-color-gray-2)',
		},
	}),
	NumberInput: NumberInput.extend({
		defaultProps: {
			size: 'md',
			radius: 0,
			decimalSeparator: ',',
			allowDecimal: false,
			allowNegative: false,
		},
		classNames: {
			label: styles.selectLabel,
		},
	}),
	DatePickerInput: DatePickerInput.extend({
		defaultProps: {
			withAsterisk: false,
			size: 'md',
			radius: 0,
			valueFormat: 'DD.MM.YYYY',
			popoverProps: {
				zIndex: APP_CONFIG.Z_INDEX.SELECT_DROPDOWN,
			},
		},
		classNames: {
			monthCell: styles.datePickerMonthCell,
			label: styles.datePickerInputLabel,
		},
	}),
	Select: Select.extend({
		defaultProps: {
			allowDeselect: false,
			withCheckIcon: false,
			rightSectionWidth: 30,
			size: 'md',
			radius: 0,
			searchable: true,
			comboboxProps: {
				zIndex: APP_CONFIG.Z_INDEX.SELECT_DROPDOWN,
				radius: 0,
				transitionProps: {
					duration: 100,
					transition: 'scale-y',
					timingFunction: 'ease',
				},
			},
		},
		classNames: {
			input: styles.selectInput,
			section: styles.selectSection,
			option: styles.selectOption,
			dropdown: styles.selectDropdown,
			empty: styles.selectEmpty,
			error: styles.textInputError,
			label: styles.selectLabel,
		},
	}),
	Avatar: Avatar.extend({
		classNames: {
			root: styles.avatarRoot,
		},
	}),
	Image: Image.extend({
		defaultProps: {
			fit: 'contain',
		},
		classNames: {
			root: 'fade-in',
		},
	}),
	NativeSelect: NativeSelect.extend({
		defaultProps: {},
		classNames: {
			input: styles.nativeSelectInput,
		},
	}),
	ScrollArea: ScrollArea.extend({
		defaultProps: {
			scrollbarSize: 7,
			type: 'auto',
			['data-scrollarea']: 'true',
		},
		classNames: {
			scrollbar: styles.scrollAreaScrollbar,
			thumb: styles.scrollAreaThumb,
		},
	}),
	ScrollAreaAutosize: ScrollAreaAutosize.extend({
		defaultProps: {
			scrollbarSize: 7,
			type: 'auto',
		},
		classNames: {
			scrollbar: styles.scrollAreaScrollbar,
			thumb: styles.scrollAreaThumb,
		},
	}),
	Tooltip: Tooltip.extend({
		defaultProps: {
			position: 'top',
			transitionProps: {
				transition: 'pop',
				duration: 200,
			},
			multiline: true,
			withArrow: true,
			arrowSize: 8,
			events: { hover: true, focus: true, touch: true },
			color: 'black',
			radius: 0,
		},
		classNames: {
			tooltip: styles.tooltipTooltip,
		},
	}),
	Group: Group.extend({
		classNames: {
			root: styles.groupRoot,
		},
	}),
	RadioGroup: RadioGroup.extend({
		defaultProps: {
			size: 'xs',
		},
	}),
	Radio: Radio.extend({
		defaultProps: {
			size: 'xs',
		},
		classNames: {
			root: styles.radioRoot,
			body: styles.radioBody,
			labelWrapper: styles.radioLabelWrapper,
			label: styles.radioLabel,
			inner: styles.radioInner,
			radio: styles.radioRadio,
			icon: styles.radioIcon,
		},
	}),
	Anchor: Anchor.extend({
		defaultProps: {
			underline: 'never',
			c: 'dark.9',
		},
	}),
	Stack: Stack.extend({
		classNames: {
			root: styles.stackRoot,
		},
	}),
	Checkbox: Checkbox.extend({
		classNames: {},
		defaultProps: {
			radius: 0,
			size: 'md',
		},
	}),
	Input: Input.extend({
		defaultProps: {
			radius: 0,
		},
	}),
	DateInput: DateInput.extend({
		defaultProps: {
			radius: 0,
			size: 'md',
			pointer: true,
		},
		classNames: {
			label: styles.textInputLabel,
		},
	}),
	TextInput: TextInput.extend({
		defaultProps: {
			withAsterisk: false,
			radius: 0,
			size: 'md',
		},
		classNames: {
			input: styles.textInputInput,
			error: styles.textInputError,
			label: styles.textInputLabel,
		},
	}),
	FileInput: FileInput.extend({
		defaultProps: {
			radius: 0,
			size: 'md',
		},
		classNames: {
			input: styles.textInputInput,
			error: styles.textInputError,
			label: styles.textInputLabel,
		},
	}),
	Textarea: Textarea.extend({
		defaultProps: {
			withAsterisk: false,
			radius: 0,
			autosize: true,
			minRows: 4,
			maxRows: 8,
			size: 'md',
		},
		classNames: {
			input: styles.textInputInput,
			label: styles.textInputLabel,
		},
	}),
	PasswordInput: PasswordInput.extend({
		defaultProps: {
			size: 'md',
			visibilityToggleIcon: ({ reveal }) => (
				<Center
					className={classNames({
						[styles.passwordInputIconRevealed]: reveal,
					})}
				>
					<IconEye color="var(--mantine-color-dimmed)" />
				</Center>
			),
		},
		classNames: {
			input: styles.textInputInput,
			error: styles.textInputError,
			label: styles.textInputLabel,
			section: styles.passwordInputSection,
		},
	}),
	Loader: MantineLoader.extend({
		defaultProps: {
			loaders: { ...MantineLoader.defaultLoaders, spinner: Loader },
			type: 'spinner',
		},
	}),
	LoadingOverlay: LoadingOverlay.extend({
		defaultProps: {
			loaderProps: {
				type: 'spinner',
			},
			overlayProps: {
				opacity: 0.5,
			},
			zIndex: APP_CONFIG.Z_INDEX.OVERLAY,
		},
	}),
	Overlay: Overlay.extend({
		defaultProps: {
			zIndex: APP_CONFIG.Z_INDEX.OVERLAY,
		},
	}),
	Progress: Progress.extend({
		defaultProps: {
			size: 'md',
			color: 'green.6',
			radius: 0,
		},
		styles: ({ colors }) => ({
			root: {
				backgroundColor: colors.green[0],
			},
		}),
	}),
	Slider: Slider.extend({
		defaultProps: {
			labelTransitionProps: {
				transition: 'fade',
				duration: 250,
			},
			thumbSize: 22,
			size: 6,
			label: null,
		},
		classNames: {
			bar: styles.sliderBar,
			thumb: styles.sliderThumb,
			track: styles.sliderTrack,
			label: styles.sliderLabel,
		},
	}),
	Timeline: Timeline.extend({
		defaultProps: {
			bulletSize: 30,
			lineWidth: 3,
		},
		classNames: {
			itemTitle: styles.timelineItemTitle,
			itemBullet: styles.timelineItemBullet,
		},
	}),
	DatePicker: DatePicker.extend({
		classNames: {
			day: styles.datePickerDay,
		},
	}),
	Drawer: Drawer.extend({
		defaultProps: {
			position: 'right',
			zIndex: APP_CONFIG.Z_INDEX.DRAWER,
			size: 592,
			closeButtonProps: {
				iconSize: 20,
			},
		},
		classNames: {
			overlay: styles.drawerInner,
			inner: styles.drawerInner,
			body: styles.drawerBody,
			header: styles.drawerHeader,
			title: styles.drawerTitle,
			close: styles.drawerClose,
		},
	}),
	Title: Title.extend({
		defaultProps: {
			lts: -0.25,
			style: {
				textWrap: 'balance',
			},
		},
	}),
	Notification: Notification.extend({
		defaultProps: { withCloseButton: true, py: 'md', px: 'sm', radius: 0 },
		styles: {
			root: {
				zIndex: APP_CONFIG.Z_INDEX.NOTIFICATION,
			},
		},
		classNames: {
			root: styles.notificationRoot,
			title: styles.notificationTitle,
			body: styles.notificationBody,
			description: styles.notificationDescription,
			closeButton: styles.notificationCloseButton,
		},
	}),
	BackgroundImage: BackgroundImage.extend({
		defaultProps: {
			bgr: 'no-repeat',
			bgsz: 'contain',
			h: '100%',
		},
	}),
	Accordion: Accordion.extend({
		defaultProps: {
			chevronPosition: 'left',
			chevronSize: 20,
			transitionDuration: 250,
		},
		classNames: {
			content: styles.accordionContent,
			item: styles.accordionItem,
			control: styles.accordionControl,
			label: styles.accordionLabel,
			chevron: styles.accordionChevron,
		},
	}),
	Popover: Popover.extend({
		defaultProps: {
			shadow: 'md',
			withArrow: false,
		},
		classNames: {
			dropdown: styles.popoverDropdown,
		},
	}),
	Dropzone: Dropzone.extend({
		defaultProps: {
			bg: 'gray.0',
			bd: 'dashed 1px var(--mantine-color-gray-4)',
			style: {
				cursor: 'pointer',
			},
		},
	}),
	List: List.extend({
		classNames: {
			itemLabel: styles.listItemLabel,
		},
	}),
	Stepper: Stepper.extend({
		defaultProps: {
			radius: 'xl',
			iconSize: 24,
		},
		classNames: {
			separator: styles.stepperSeparator,
			root: styles.stepperRoot,
			steps: styles.stepperSteps,
			step: styles.stepperStep,
			stepIcon: styles.stepperStepIcon,
		},
	}),
};

const fontSizes: MantineTheme['fontSizes'] = {
	xs: rem(11),
	sm: rem(12),
	md: rem(14),
	lg: rem(16),
	xl: rem(18),
};

const lineHeights: MantineTheme['lineHeights'] = {
	xs: rem(18),
	sm: rem(18),
	md: rem(20),
	lg: rem(24),
	xl: rem(30),
};

const headings: MantineThemeOverride['headings'] = {
	fontFamily: 'Inter, sans-serif',
	fontWeight: '600',
	sizes: {},
};

const breakpoints = Object.entries(APP_CONFIG.BREAKPOINTS).reduce<
	MantineThemeOverride['breakpoints']
>((acc, curr) => {
	acc![curr[0].toLowerCase()] = curr[1];
	return acc;
}, {});

const spacing: MantineThemeOverride['spacing'] = {
	xs: rem(3),
	sm: rem(8),
	md: rem(16),
	lg: rem(24),
	xl: rem(32),
};

const other: MantineThemeOverride['other'] = {
	notificationProps: {
		info: {
			classNames: {
				root: styles.notificationInfo,
				icon: styles.notificationInfoIcon,
				description: styles.notificationInfoDescription,
			},
			icon: <IconInfoCircle color="var(--mantine-color-blue-6)" />,
		},
		danger: {
			classNames: {
				root: styles.notificationDanger,
				icon: styles.notificationDangerIcon,
				description: styles.notificationDangerDescription,
			},
			icon: <IconExclamationCircle color="var(--mantine-color-red-8)" />,
		},
		success: {
			classNames: {
				root: styles.notificationSuccess,
				icon: styles.notificationSuccessIcon,
				description: styles.notificationSuccessDescription,
			},
			icon: <IconCheck color="var(--mantine-color-teal-6)" />,
		},
	},
};

export const MantineProvider = ({ children }: { children: ReactNode }) => {
	return (
		<Mantine
			theme={{
				fontFamily: 'Inter, sans-serif',
				black: '#121926',
				primaryColor: 'red',
				primaryShade: 7,
				lineHeights,
				spacing,
				other,
				cursorType: 'pointer',
				components,
				headings,
				breakpoints,
				fontSizes,
				colors,
			}}
		>
			<ModalsProvider>{children}</ModalsProvider>
		</Mantine>
	);
};
